import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, X, Loader2, Link2, Clipboard } from "lucide-react";
import { toast } from "sonner";

export type ImageMeta = Record<string, { title?: string; description?: string }>;

interface Props {
  kind: "image" | "video";
  max: number;
  value: string[];
  onChange: (urls: string[]) => void;
  folder?: string;
  meta?: ImageMeta;
  onMetaChange?: (meta: ImageMeta) => void;
}

export const isValidMediaUrl = (v: string) => {
  try { const u = new URL(v.trim()); return u.protocol === "https:" || u.protocol === "http:"; } catch { return false; }
};

/** Adds media by upload, link or Ctrl+V paste to the public cms-images bucket. */
const MediaUploader = ({ kind, max, value, onChange, folder = "products", meta, onMetaChange }: Props) => {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  const remaining = max - value.length;
  const label = kind === "image" ? "fotos" : "vídeos";

  const uploadFiles = async (files: File[]) => {
    if (!files.length) return;
    const list = files.slice(0, remaining);
    if (files.length > remaining) toast.error(`Máximo de ${max} ${label}`);
    if (!list.length) return;
    setBusy(true);
    const urls: string[] = [];
    for (const f of list) {
      const ext = f.name.includes(".") ? f.name.split(".").pop() : (f.type.split("/")[1] || "png");
      const path = `${folder}/${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage.from("cms-images").upload(path, f, { contentType: f.type });
      if (error) { toast.error(`Erro ao enviar ${f.name}: ${error.message}`); continue; }
      urls.push(supabase.storage.from("cms-images").getPublicUrl(path).data.publicUrl);
    }
    onChange([...value, ...urls]);
    setBusy(false);
  };

  const addLink = (raw: string) => {
    const url = raw.trim();
    if (!isValidMediaUrl(url)) { toast.error("Link inválido. Use um endereço que comece por https://"); return; }
    if (value.includes(url)) { toast.error("Este link já foi adicionado"); return; }
    if (remaining <= 0) { toast.error(`Máximo de ${max} ${label}`); return; }
    onChange([...value, url]);
    setLink("");
  };

  const onPaste = (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith(kind === "image" ? "image/" : "video/"));
    if (files.length) { e.preventDefault(); uploadFiles(files); return; }
    const text = e.clipboardData.getData("text");
    if (text && isValidMediaUrl(text)) { e.preventDefault(); addLink(text); }
  };

  const remove = (i: number) => {
    const url = value[i];
    onChange(value.filter((_, j) => j !== i));
    if (meta && onMetaChange && meta[url]) { const next = { ...meta }; delete next[url]; onMetaChange(next); }
  };

  const move = (i: number) => {
    if (i === 0) return;
    const next = [...value];
    [next[0], next[i]] = [next[i], next[0]];
    onChange(next);
  };

  const setMeta = (url: string, field: "title" | "description", v: string) =>
    onMetaChange?.({ ...(meta || {}), [url]: { ...(meta?.[url] || {}), [field]: v } });

  return (
    <div className="space-y-3" onPaste={onPaste}>
      <div className="space-y-2">
        {value.map((url, i) => (
          <div key={url} className="flex gap-3 items-start">
            <div className="relative rounded-lg overflow-hidden border border-border w-20 h-20 shrink-0 bg-muted">
              {kind === "image" ? (
                <button type="button" onClick={() => move(i)} title="Definir como principal" className="w-full h-full">
                  <img src={url} alt={meta?.[url]?.title || ""} className="w-full h-full object-cover" />
                </button>
              ) : (
                <video src={url} className="w-full h-full object-cover" muted />
              )}
              {kind === "image" && i === 0 && (
                <span className="absolute bottom-1 left-1 text-[10px] bg-primary text-primary-foreground px-1.5 rounded">Principal</span>
              )}
              <button type="button" onClick={() => remove(i)} className="absolute top-1 right-1 bg-background/90 rounded-full p-1" aria-label="Remover">
                <X size={12} />
              </button>
            </div>
            {onMetaChange && (
              <div className="flex-1 space-y-1.5 min-w-0">
                <Input placeholder="Título da imagem" value={meta?.[url]?.title || ""} onChange={(e) => setMeta(url, "title", e.target.value)} />
                <Input placeholder="Descrição da imagem (para o Google)" value={meta?.[url]?.description || ""} onChange={(e) => setMeta(url, "description", e.target.value)} />
              </div>
            )}
          </div>
        ))}
      </div>
      {remaining > 0 && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder={`Colar link ou Ctrl+V de ${kind === "image" ? "uma imagem" : "um vídeo"}`}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLink(link); } }} />
            <Button type="button" variant="outline" size="sm" className="gap-1 shrink-0" onClick={() => addLink(link)}>
              <Link2 size={14} /> Adicionar
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" className="gap-2" disabled={busy} asChild>
              <label className="cursor-pointer">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {busy ? "A enviar..." : `Carregar ${label} (${value.length}/${max})`}
                <input type="file" hidden multiple accept={kind === "image" ? "image/*" : "video/*"}
                  onChange={(e) => { uploadFiles(Array.from(e.target.files || [])); e.target.value = ""; }} />
              </label>
            </Button>
            <span className="text-xs text-muted-foreground flex items-center gap-1"><Clipboard size={12} /> Também pode colar com Ctrl+V</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
